// src/services/userService.js - Part 1: Core functionality and validation
import { apiService } from "./api";

// User validation schemas
const USER_VALIDATION_RULES = {
  username: {
    minLength: 3,
    maxLength: 50,
    pattern: /^[a-zA-Z0-9_.-]+$/,
    reservedWords: ['admin', 'root', 'system', 'test', 'null', 'undefined']
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    maxLength: 100
  },
  password: {
    minLength: 8,
    maxLength: 128,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/
  },
  fullName: {
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s'-]+$/
  },
  phoneNumber: {
    pattern: /^\+?[\d\s\-\(\)]+$/,
    minLength: 10,
    maxLength: 20
  }
};

// User roles with permissions
const USER_ROLES = {
  SUPER_ADMIN: {
    value: 'super_admin',
    level: 100,
    label: 'Super Administrator'
  },
  ADMIN: {
    value: 'admin',
    level: 80,
    label: 'Administrator'
  },
  FACILITY_ADMIN: {
    value: 'facility_admin',
    level: 60,
    label: 'Facility Administrator'
  },
  MEDICAL_PROFESSIONAL: {
    value: 'medical_professional',
    level: 40,
    label: 'Medical Professional'
  },
  USER: {
    value: 'user',
    level: 20,
    label: 'User'
  }
};

export const userService = {
  // Get medical professionals with filtering
  getMedicalProfessionals: async (params = {}) => {
    try {
      const queryParams = {
        skip: params.skip || 0,
        limit: params.limit || 100,
        facility_id: params.facilityId,
        category: params.category,
        status: params.status || "active",
        search: params.search,
      };

      // Remove undefined values
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });

      const response = await apiService.get("/medical-professionals", {
        params: queryParams,
        useCache: true,
      });

      return response.data || [];
    } catch (error) {
      console.warn("Error fetching medical professionals:", error.message);
      return [];
    }
  },

  // Get all users with pagination
  getAllUsers: async (params = {}) => {
    try {
      const queryParams = {
        skip: params.skip || 0,
        limit: params.limit || 100,
        role: params.role,
        facility_id: params.facilityId,
        status: params.status,
        search: params.search,
        sort_by: params.sortBy || "created_at",
        sort_order: params.sortOrder || "desc",
      };

      // Remove undefined values
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });

      const response = await apiService.get("/users", {
        params: queryParams,
        useCache: true,
      });

      return response.data || [];
    } catch (error) {
      console.warn("Error fetching all users:", error.message);

      // Fallback data for development
      if (error.response?.status === 404 || error.response?.status === 500) {
        return [
          {
            id: 1,
            username: "admin",
            email: "admin@medflowhub.com",
            full_name: "System Administrator",
            role: "super_admin",
            status: "active",
            is_active: true,
            facility_id: 1,
            created_at: new Date().toISOString(),
          },
        ];
      }
      throw error;
    }
  },

  // Get pending users
  getPendingUsers: async () => {
    try {
      const response = await apiService.get("/users/pending-approval", {
        useCache: false,
      });

      return response.data || [];
    } catch (error) {
      console.warn("Error fetching pending users:", error.message);
      return [];
    }
  },

  // Approve user
  approveUser: async (userId, approvalData = {}) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.put(`/users/approve/${userId}`, {
        approved_by: approvalData.approvedBy,
        approval_notes: approvalData.notes,
        approved_at: new Date().toISOString(),
        ...approvalData,
      });

      return response.data;
    } catch (error) {
      console.error(`Error approving user ${userId}:`, error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to approve user: ${error.message}`
      );
    }
  },

  // Reject user
  rejectUser: async (userId, rejectionData = {}) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.put(`/users/reject/${userId}`, {
        rejected_by: rejectionData.rejectedBy,
        rejection_reason: rejectionData.reason || "No reason provided",
        rejected_at: new Date().toISOString(),
        ...rejectionData,
      });

      return response.data;
    } catch (error) {
      console.error(`Error rejecting user ${userId}:`, error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to reject user: ${error.message}`
      );
    }
  },

  // Create user with comprehensive validation
  createUser: async (userData) => {
    try {
      // Comprehensive validation
      const validation = userService.validateUserData(userData);
      if (!validation.isValid) {
        throw new Error(
          `Validation failed: ${Object.values(validation.errors).join(", ")}`
        );
      }

      // Prepare user data for API
      const apiUserData = {
        username: userData.username.toLowerCase().trim(),
        email: userData.email.toLowerCase().trim(),
        password: userData.password,
        full_name: userData.full_name.trim(),
        phone_number: userData.phone_number?.trim() || null,
        address: userData.address?.trim() || null,
        role: userData.role,
        facility_id: userData.facility_id
          ? parseInt(userData.facility_id)
          : null,

        // Admin-specific fields
        company_name: userData.company_name?.trim() || null,
        business_license: userData.business_license?.trim() || null,

        // Medical professional fields
        specialty: userData.specialty?.trim() || null,
        medical_license: userData.medical_license?.trim() || null,
        mp_category: userData.mp_category || null,

        // Email settings
        send_welcome_email: userData.send_welcome_email || false,
        send_activation_email: userData.send_activation_email || false,

        // Audit fields
        created_by: userData.created_by,
        created_at: new Date().toISOString(),
      };

      const response = await apiService.post("/users/create", apiUserData);

      // Handle security settings separately to avoid transaction rollback
      if (userData.vpn_required || userData.allowed_ip_ranges) {
        try {
          await userService.updateUserSecurity(response.data.id, {
            vpn_required: userData.vpn_required || false,
            allowed_ip_ranges: userData.allowed_ip_ranges
              ? userData.allowed_ip_ranges.split(",").map((ip) => ip.trim())
              : [],
          });
        } catch (securityError) {
          console.warn(
            "User created but security settings failed:",
            securityError
          );
        }
      }

      return response.data;
    } catch (error) {
      console.error("Error creating user:", error);

      // Enhanced error handling with specific messages
      if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail?.includes("Email already registered")) {
          throw new Error("This email address is already registered");
        } else if (detail?.includes("Username already taken")) {
          throw new Error("This username is already taken");
        }
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to create users");
      }

      throw error;
    }
  },

  // Update user
  updateUser: async (userId, userData, options = {}) => {
    try {
      userService.validateUserId(userId);

      // Validate only provided fields
      const validation = userService.validateUserDataPartial(userData);
      if (!validation.isValid) {
        throw new Error(
          `Validation failed: ${Object.values(validation.errors).join(", ")}`
        );
      }

      // Prepare update data
      const updateData = {
        ...userData,
        updated_at: new Date().toISOString(),
        updated_by: options.updatedBy,
      };

      // Clean up undefined values
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      const response = await apiService.put(`/users/${userId}`, updateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating user ${userId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("User not found");
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to update this user");
      }

      throw error;
    }
  },

  // Get user by ID
  getUserById: async (userId, useCache = true) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.get(`/users/${userId}`, { useCache });
      return response.data;
    } catch (error) {
      console.warn(`Error fetching user ${userId}:`, error.message);

      if (error.response?.status === 404) {
        throw new Error("User not found");
      }

      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await apiService.get("/users/me", { useCache: true });
      return response.data;
    } catch (error) {
      console.warn("Error fetching current user:", error.message);

      // Try to get from auth service as fallback
      try {
        const authService = await import("./authService");
        const cachedUser = authService.default.getCurrentUserFromStorage();
        if (cachedUser) {
          return cachedUser;
        }
      } catch (authError) {
        console.warn("Could not get user from auth service:", authError);
      }

      throw error;
    }
  },

  // Delete user
  deleteUser: async (userId, options = {}) => {
    try {
      userService.validateUserId(userId);

      const deleteData = {
        soft_delete: options.softDelete !== false,
        deletion_reason: options.reason || "Manual deletion",
        deleted_by: options.deletedBy,
        deleted_at: new Date().toISOString(),
      };

      const response = await apiService.delete(`/users/${userId}`, {
        data: deleteData,
      });

      return response.data;
    } catch (error) {
      console.error(`Error deleting user ${userId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("User not found");
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to delete this user");
      } else if (error.response?.status === 409) {
        throw new Error("Cannot delete user with active associations");
      }

      // Development fallback
      if (error.response?.status === 501 || error.response?.status === 405) {
        console.warn("Delete endpoint not implemented yet, simulating success");
        return {
          message: "User deleted successfully (simulated)",
          id: userId,
        };
      }

      throw error;
    }
  },

  // User activation
  activateUser: async (userId, activationData = {}) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.put(`/users/${userId}/activate`, {
        activated_by: activationData.activatedBy,
        activation_notes: activationData.notes,
        send_notification: activationData.sendNotification !== false,
        activated_at: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      console.error(`Error activating user ${userId}:`, error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to activate user: ${error.message}`
      );
    }
  },

  // User deactivation
  deactivateUser: async (userId, deactivationData = {}) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.put(`/users/${userId}/deactivate`, {
        deactivated_by: deactivationData.deactivatedBy,
        deactivation_reason: deactivationData.reason || "Manual deactivation",
        send_notification: deactivationData.sendNotification !== false,
        deactivated_at: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      console.error(`Error deactivating user ${userId}:`, error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to deactivate user: ${error.message}`
      );
    }
  },

  // Password reset
  resetUserPassword: async (userId, resetData = {}) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.post(
        `/users/${userId}/reset-password`,
        {
          temporary_password: resetData.temporaryPassword,
          require_change_on_login: resetData.requireChangeOnLogin !== false,
          send_email: resetData.sendEmail !== false,
          reset_by: resetData.resetBy,
          reset_reason: resetData.reason || "Password reset requested",
        }
      );

      return response.data;
    } catch (error) {
      console.error(`Error resetting password for user ${userId}:`, error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to reset password: ${error.message}`
      );
    }
  },

  // Send password reset email
  sendPasswordResetEmail: async (email, resetData = {}) => {
    try {
      if (!email || !USER_VALIDATION_RULES.email.pattern.test(email)) {
        throw new Error("Valid email address is required");
      }

      const response = await apiService.post("/users/send-password-reset", {
        email: email.toLowerCase().trim(),
        reset_url: resetData.resetUrl,
        expires_in: resetData.expiresIn || 3600,
        language: resetData.language || "en",
      });

      return response.data;
    } catch (error) {
      console.error("Error sending password reset email:", error);

      if (error.response?.status === 429) {
        throw new Error(
          "Too many password reset requests. Please wait before trying again."
        );
      } else if (error.response?.status === 404) {
        // Don't reveal if email exists for security
        return {
          success: true,
          message: "If the email exists, a reset link has been sent.",
        };
      }

      throw new Error(
        error.response?.data?.detail ||
          `Failed to send password reset email: ${error.message}`
      );
    }
  },

  // Medical professional assignment
  assignMedicalProfessional: async (mpId, adminId, assignmentData = {}) => {
    try {
      userService.validateUserId(mpId);
      userService.validateUserId(adminId);

      const response = await apiService.put(
        `/users/medical-professionals/${mpId}/assign/${adminId}`,
        {
          assigned_by: assignmentData.assignedBy,
          assignment_notes: assignmentData.notes,
          effective_date:
            assignmentData.effectiveDate || new Date().toISOString(),
          ...assignmentData,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error assigning medical professional:", error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to assign medical professional: ${error.message}`
      );
    }
  },

  // Get admin medical professionals
  getAdminMedicalProfessionals: async (adminId, params = {}) => {
    try {
      userService.validateUserId(adminId);

      const queryParams = {
        ...params,
        admin_id: adminId,
      };

      const response = await apiService.get(
        `/users/admins/${adminId}/medical-professionals`,
        { params: queryParams, useCache: true }
      );

      return response.data || [];
    } catch (error) {
      console.warn(
        `Error fetching medical professionals for admin ${adminId}:`,
        error.message
      );
      return [];
    }
  },

  // Security management
  updateUserSecurity: async (userId, securityData) => {
    try {
      userService.validateUserId(userId);
      userService.validateSecurityData(securityData);

      const response = await apiService.put(`/users/${userId}/security`, {
        ...securityData,
        updated_at: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      console.error(`Error updating security for user ${userId}:`, error);

      // For development, provide fallback success
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.warn("Security endpoint not available, simulating success");
        return {
          message: "Security settings updated (simulated)",
          vpn_required: securityData.vpn_required,
          allowed_ip_ranges: securityData.allowed_ip_ranges,
        };
      }

      throw new Error(
        error.response?.data?.detail ||
          `Failed to update security settings: ${error.message}`
      );
    }
  },

  // Get user security
  getUserSecurity: async (userId) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.get(`/users/${userId}/security`, {
        useCache: true,
      });

      return response.data;
    } catch (error) {
      console.warn(
        `Error fetching security for user ${userId}:`,
        error.message
      );

      // Return default security settings
      return {
        vpn_required: false,
        allowed_ip_ranges: [],
        sms_2fa_enabled: false,
        totp_2fa_enabled: false,
        api_key_configured: false,
        last_login: null,
        failed_attempts: 0,
        account_locked: false,
      };
    }
  },

  searchUsers: async (searchParams = {}) => {
    try {
      const params = {
        q: searchParams.query,
        role: searchParams.role,
        facility_id: searchParams.facilityId,
        status: searchParams.status,
        created_after: searchParams.createdAfter,
        created_before: searchParams.createdBefore,
        skip: searchParams.skip || 0,
        limit: searchParams.limit || 50,
        sort_by: searchParams.sortBy || "relevance",
      };

      // Remove undefined values
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await apiService.get("/users/search", {
        params,
        useCache: false,
      });

      return response.data || [];
    } catch (error) {
      console.warn("Error searching users:", error.message);
      return [];
    }
  },

  // Get user stats
  getUserStats: async (facilityId = null) => {
    try {
      const params = facilityId ? { facility_id: facilityId } : {};
      const response = await apiService.get("/users/stats", {
        params,
        useCache: true,
      });

      return response.data;
    } catch (error) {
      console.warn("Error fetching user stats:", error.message);
      return {
        total_users: 0,
        active_users: 0,
        pending_users: 0,
        by_role: {},
        recent_signups: 0,
      };
    }
  },

  // Comprehensive user data validation
  validateUserData: (userData) => {
    const errors = {};

    // Username validation
    if (!userData.username?.trim()) {
      errors.username = "Username is required";
    } else {
      const username = userData.username.trim();
      if (username.length < USER_VALIDATION_RULES.username.minLength) {
        errors.username = `Username must be at least ${USER_VALIDATION_RULES.username.minLength} characters`;
      } else if (username.length > USER_VALIDATION_RULES.username.maxLength) {
        errors.username = `Username must be no more than ${USER_VALIDATION_RULES.username.maxLength} characters`;
      } else if (!USER_VALIDATION_RULES.username.pattern.test(username)) {
        errors.username =
          "Username can only contain letters, numbers, dots, hyphens, and underscores";
      } else if (
        USER_VALIDATION_RULES.username.reservedWords.includes(
          username.toLowerCase()
        )
      ) {
        errors.username = "This username is reserved and cannot be used";
      }
    }

    // Email validation
    if (!userData.email?.trim()) {
      errors.email = "Email is required";
    } else {
      const email = userData.email.trim();
      if (!USER_VALIDATION_RULES.email.pattern.test(email)) {
        errors.email = "Invalid email format";
      } else if (email.length > USER_VALIDATION_RULES.email.maxLength) {
        errors.email = `Email must be no more than ${USER_VALIDATION_RULES.email.maxLength} characters`;
      }
    }

    // Password validation (only if provided)
    if (userData.password && !userData.send_activation_email) {
      const password = userData.password;
      if (password.length < USER_VALIDATION_RULES.password.minLength) {
        errors.password = `Password must be at least ${USER_VALIDATION_RULES.password.minLength} characters`;
      } else if (password.length > USER_VALIDATION_RULES.password.maxLength) {
        errors.password = `Password must be no more than ${USER_VALIDATION_RULES.password.maxLength} characters`;
      } else if (!USER_VALIDATION_RULES.password.pattern.test(password)) {
        errors.password =
          "Password must contain uppercase, lowercase, numbers, and special characters";
      }
    } else if (!userData.password && !userData.send_activation_email) {
      errors.password =
        "Password is required when not sending activation email";
    }

    // Full name validation
    if (!userData.full_name?.trim()) {
      errors.full_name = "Full name is required";
    } else {
      const fullName = userData.full_name.trim();
      if (fullName.length < USER_VALIDATION_RULES.fullName.minLength) {
        errors.full_name = `Full name must be at least ${USER_VALIDATION_RULES.fullName.minLength} characters`;
      } else if (fullName.length > USER_VALIDATION_RULES.fullName.maxLength) {
        errors.full_name = `Full name must be no more than ${USER_VALIDATION_RULES.fullName.maxLength} characters`;
      } else if (!USER_VALIDATION_RULES.fullName.pattern.test(fullName)) {
        errors.full_name =
          "Full name can only contain letters, spaces, hyphens, and apostrophes";
      }
    }

    // Role validation
    if (!userData.role) {
      errors.role = "Role is required";
    } else if (
      !Object.values(USER_ROLES).some((role) => role.value === userData.role)
    ) {
      errors.role = "Invalid role specified";
    }

    // Phone number validation (if provided)
    if (userData.phone_number) {
      const phone = userData.phone_number.trim();
      if (
        phone.length < USER_VALIDATION_RULES.phoneNumber.minLength ||
        phone.length > USER_VALIDATION_RULES.phoneNumber.maxLength
      ) {
        errors.phone_number = `Phone number must be between ${USER_VALIDATION_RULES.phoneNumber.minLength} and ${USER_VALIDATION_RULES.phoneNumber.maxLength} characters`;
      } else if (!USER_VALIDATION_RULES.phoneNumber.pattern.test(phone)) {
        errors.phone_number = "Invalid phone number format";
      }
    }

    // Role-specific validation
    if (userData.role === "medical_professional") {
      if (!userData.specialty?.trim()) {
        errors.specialty = "Specialty is required for medical professionals";
      }
      if (!userData.mp_category) {
        errors.mp_category = "Category is required for medical professionals";
      }
    }

    if (["admin", "facility_admin"].includes(userData.role)) {
      if (!userData.company_name?.trim()) {
        errors.company_name = "Company name is required for administrators";
      }
    }

    // Facility validation
    if (!userData.facility_id && userData.role !== "super_admin") {
      errors.facility_id = "Facility assignment is required";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Partial validation for updates
  validateUserDataPartial: (userData) => {
    const errors = {};

    // Only validate provided fields
    if (userData.username !== undefined) {
      if (!userData.username?.trim()) {
        errors.username = "Username cannot be empty";
      } else {
        const username = userData.username.trim();
        if (username.length < USER_VALIDATION_RULES.username.minLength) {
          errors.username = `Username must be at least ${USER_VALIDATION_RULES.username.minLength} characters`;
        } else if (!USER_VALIDATION_RULES.username.pattern.test(username)) {
          errors.username =
            "Username can only contain letters, numbers, dots, hyphens, and underscores";
        }
      }
    }

    if (userData.email !== undefined) {
      if (!userData.email?.trim()) {
        errors.email = "Email cannot be empty";
      } else if (
        !USER_VALIDATION_RULES.email.pattern.test(userData.email.trim())
      ) {
        errors.email = "Invalid email format";
      }
    }

    if (userData.password !== undefined && userData.password) {
      if (userData.password.length < USER_VALIDATION_RULES.password.minLength) {
        errors.password = `Password must be at least ${USER_VALIDATION_RULES.password.minLength} characters`;
      } else if (
        !USER_VALIDATION_RULES.password.pattern.test(userData.password)
      ) {
        errors.password =
          "Password must contain uppercase, lowercase, numbers, and special characters";
      }
    }

    if (userData.full_name !== undefined) {
      if (!userData.full_name?.trim()) {
        errors.full_name = "Full name cannot be empty";
      } else if (
        !USER_VALIDATION_RULES.fullName.pattern.test(userData.full_name.trim())
      ) {
        errors.full_name =
          "Full name can only contain letters, spaces, hyphens, and apostrophes";
      }
    }

    if (userData.phone_number !== undefined && userData.phone_number) {
      if (
        !USER_VALIDATION_RULES.phoneNumber.pattern.test(userData.phone_number)
      ) {
        errors.phone_number = "Invalid phone number format";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Validation helpers
  validateUserId: (userId) => {
    if (!userId) {
      throw new Error("User ID is required");
    }
    if (typeof userId !== "number" && typeof userId !== "string") {
      throw new Error("User ID must be a number or string");
    }
    if (typeof userId === "string" && !/^\d+$/.test(userId)) {
      throw new Error("User ID must be numeric");
    }
  },

  validateSecurityData: (securityData) => {
    if (typeof securityData !== "object" || securityData === null) {
      throw new Error("Security data must be an object");
    }

    if (
      securityData.allowed_ip_ranges &&
      !Array.isArray(securityData.allowed_ip_ranges)
    ) {
      throw new Error("Allowed IP ranges must be an array");
    }

    if (securityData.allowed_ip_ranges) {
      securityData.allowed_ip_ranges.forEach((range) => {
        if (typeof range !== "string" || !range.trim()) {
          throw new Error("Each IP range must be a non-empty string");
        }
        // Basic IP/CIDR validation
        const ipPattern = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
        if (!ipPattern.test(range.trim())) {
          throw new Error(`Invalid IP range format: ${range}`);
        }
      });
    }
  },

  // Utility methods
  getRoleHierarchy: () => {
    return Object.values(USER_ROLES).sort((a, b) => b.level - a.level);
  },

  getRolePermissions: (role) => {
    const roleObj = Object.values(USER_ROLES).find((r) => r.value === role);
    return roleObj ? roleObj.permissions || [] : [];
  },

  hasPermission: (userRole, permission) => {
    const permissions = userService.getRolePermissions(userRole);
    return permissions.includes("*") || permissions.includes(permission);
  },

  canUserManageRole: (managerRole, targetRole) => {
    const manager = Object.values(USER_ROLES).find(
      (r) => r.value === managerRole
    );
    const target = Object.values(USER_ROLES).find(
      (r) => r.value === targetRole
    );

    if (!manager || !target) return false;
    return manager.level > target.level;
  },

  // Helper methods for UI
  getAvailableRoles: (currentUserRole = null) => {
    const allRoles = Object.values(USER_ROLES);

    if (!currentUserRole) {
      return allRoles.map((role) => ({
        value: role.value,
        label: role.label,
      }));
    }

    // Filter roles based on current user's permissions
    const currentRole = allRoles.find((r) => r.value === currentUserRole);
    if (!currentRole) return [];

    return allRoles
      .filter((role) => currentRole.level >= role.level)
      .map((role) => ({
        value: role.value,
        label: role.label,
      }));
  },

  // Get user notification preferences
  getUserNotificationPreferences: async (userId) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.get(
        `/users/${userId}/notification-preferences`,
        {
          useCache: true,
        }
      );

      return response.data;
    } catch (error) {
      console.warn(
        `Error fetching notification preferences for user ${userId}:`,
        error.message
      );
      return {
        email_notifications: true,
        sms_notifications: false,
        push_notifications: true,
        security_alerts: true,
      };
    }
  },

  // Update user notification preferences
  updateUserNotificationPreferences: async (userId, preferences) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.put(
        `/users/${userId}/notification-preferences`,
        preferences
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating notification preferences for user ${userId}:`,
        error
      );
      throw new Error(
        error.response?.data?.detail ||
          `Failed to update preferences: ${error.message}`
      );
    }
  },

  // Get user audit log
  getUserAuditLog: async (userId, params = {}) => {
    try {
      userService.validateUserId(userId);

      const queryParams = {
        skip: params.skip || 0,
        limit: params.limit || 50,
        action_type: params.actionType,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        ...params,
      };

      const response = await apiService.get(`/users/${userId}/audit-log`, {
        params: queryParams,
        useCache: false,
      });

      return response.data || [];
    } catch (error) {
      console.warn(
        `Error fetching audit log for user ${userId}:`,
        error.message
      );
      return [];
    }
  },

  // Session management
  getUserSessions: async (userId) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.get(`/users/${userId}/sessions`, {
        useCache: false,
      });

      return response.data || [];
    } catch (error) {
      console.warn(
        `Error fetching sessions for user ${userId}:`,
        error.message
      );
      return [];
    }
  },

  revokeUserSession: async (userId, sessionId) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.delete(
        `/users/${userId}/sessions/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error revoking session ${sessionId} for user ${userId}:`,
        error
      );
      throw new Error(
        error.response?.data?.detail ||
          `Failed to revoke session: ${error.message}`
      );
    }
  },

  revokeAllUserSessions: async (userId, exceptCurrent = true) => {
    try {
      userService.validateUserId(userId);

      const response = await apiService.delete(`/users/${userId}/sessions`, {
        data: { except_current: exceptCurrent },
      });

      return response.data;
    } catch (error) {
      console.error(`Error revoking all sessions for user ${userId}:`, error);
      throw new Error(
        error.response?.data?.detail ||
          `Failed to revoke sessions: ${error.message}`
      );
    }
  },

  // Batch operations
  batchUpdateUsers: async (userIds, updateData, options = {}) => {
    try {
      if (!Array.isArray(userIds) || userIds.length === 0) {
        throw new Error("User IDs array is required");
      }

      userIds.forEach((id) => userService.validateUserId(id));

      const response = await apiService.put("/users/batch-update", {
        user_ids: userIds,
        update_data: updateData,
        updated_by: options.updatedBy,
        batch_id: options.batchId || `batch_${Date.now()}`,
      });

      return response.data;
    } catch (error) {
      console.error("Error in batch update:", error);
      throw new Error(
        error.response?.data?.detail || `Batch update failed: ${error.message}`
      );
    }
  },

  // Export users
  exportUsers: async (exportParams = {}) => {
    try {
      const params = {
        format: exportParams.format || "csv",
        role: exportParams.role,
        facility_id: exportParams.facilityId,
        status: exportParams.status,
        fields: exportParams.fields?.join(","),
        ...exportParams,
      };

      const response = await apiService.get("/users/export", {
        params,
        responseType: "blob",
      });

      // Create download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `users_export_${new Date().toISOString().split("T")[0]}.${
          params.format
        }`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, message: "Users exported successfully" };
    } catch (error) {
      console.error("Error exporting users:", error);
      throw new Error(
        error.response?.data?.detail || `Export failed: ${error.message}`
      );
    }
  },

  // Enhanced error handling helper
  handleUserServiceError: (error, context = "") => {
    const baseMessage = context ? `${context}: ` : "";

    if (!error.response) {
      return `${baseMessage}Network error. Please check your connection.`;
    }

    const status = error.response.status;
    const detail = error.response.data?.detail || error.message;

    switch (status) {
      case 400:
        return `${baseMessage}Invalid request: ${detail}`;
      case 401:
        return `${baseMessage}Authentication required`;
      case 403:
        return `${baseMessage}Permission denied: ${detail}`;
      case 404:
        return `${baseMessage}User not found`;
      case 409:
        return `${baseMessage}Conflict: ${detail}`;
      case 422:
        return `${baseMessage}Validation error: ${detail}`;
      case 429:
        return `${baseMessage}Too many requests. Please wait and try again.`;
      case 500:
        return `${baseMessage}Server error. Please try again later.`;
      default:
        return `${baseMessage}${detail || "Unknown error occurred"}`;
    }
  },

  getStartingPointsSummary: async () => {
    try {
      const mps = await userService.getMPsWithStartingPoints();

      const summary = {
        total_mps: mps.length,
        with_starting_points: mps.filter((mp) => mp.has_starting_point).length,
        without_starting_points: mps.filter((mp) => !mp.has_starting_point)
          .length,
        completion_rate:
          mps.length > 0
            ? (mps.filter((mp) => mp.has_starting_point).length / mps.length) *
              100
            : 0,
        mps_needing_setup: mps
          .filter((mp) => !mp.has_starting_point)
          .map((mp) => ({
            id: mp.id,
            name: mp.user?.full_name || `MP-${mp.id}`,
          })),
      };

      return summary;
    } catch (error) {
      console.error("Error getting starting points summary:", error);
      return {
        total_mps: 0,
        with_starting_points: 0,
        without_starting_points: 0,
        completion_rate: 0,
        mps_needing_setup: [],
      };
    }
  },

  validateStartingPoint: (startingPointData) => {
    const errors = {};

    if (!startingPointData.address?.trim()) {
      errors.address = "Address is required";
    }

    if (!startingPointData.name?.trim()) {
      errors.name = "Location name is required";
    }

    if (
      startingPointData.lat &&
      (isNaN(startingPointData.lat) || Math.abs(startingPointData.lat) > 90)
    ) {
      errors.lat = "Invalid latitude";
    }

    if (
      startingPointData.lng &&
      (isNaN(startingPointData.lng) || Math.abs(startingPointData.lng) > 180)
    ) {
      errors.lng = "Invalid longitude";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Format medical professional for display
  formatMPForDisplay: (mp) => {
    return {
      ...mp,
      display_name: mp.user?.full_name || `MP-${mp.id}`,
      contact_info: {
        email: mp.user?.email,
        phone: mp.user?.phone_number,
      },
      has_starting_point: !!mp.starting_location,
      starting_point_summary: mp.starting_location
        ? `${mp.starting_location.name} (${mp.starting_location.type})`
        : "Not configured",
    };
  },

  getUserStats: async (userId) => {
    try {
      const response = await apiService.get(`/users/${userId}/stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching user stats:", error);
      return {
        total_orders: 0,
        completed_orders: 0,
        active_routes: 0,
        completion_rate: 0,
      };
    }
  },

  getMPStats: async (mpId) => {
    try {
      const response = await apiService.get(
        `/medical-professionals/${mpId}/stats`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching MP stats:", error);
      return {
        total_orders: 0,
        completed_orders: 0,
        active_routes: 0,
        completion_rate: 0,
        average_completion_time: 0,
      };
    }
  },

  resetUserPassword: async (userId) => {
    try {

      const response = await apiService.post(`/users/${userId}/reset-password`);

      return response.data;
    } catch (error) {
      console.error(`Error sending password reset for user ${userId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("User not found");
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to reset this user's password"
        );
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        throw new Error(detail || "Invalid request");
      }

      throw new Error(error.message || "Failed to send password reset email");
    }
  },
};